// src/pages/Auth/Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

function Login() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { login } = useAuth();

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
        if (errors[name] || errors.form) {
            setErrors({});
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setIsSubmitting(true);

        try {
            // Use the login function from AuthContext - it handles everything
            await login(formData.email, formData.password);
            navigate('/');
        } catch (error) {
            console.error('Login error:', error);
            
            if (error.response?.status === 401) {
                setErrors({ form: t('login.errors.invalid_credentials') });
            } else if (error.response?.data?.detail) {
                // Backend sends 'detail' field with error message
                setErrors({ form: error.response.data.detail });
            } else if (error.response?.data) {
                setErrors(error.response.data);
            } else {
                setErrors({ form: t('login.errors.unexpected_error') });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-brand-dark text-white">
            <div className="p-8 bg-brand-dark rounded-lg shadow-md w-full max-w-md border hover:border-brand-orange">
                <h2 className="text-2xl font-bold text-center mb-6">{t('login.title')}</h2>
                <form onSubmit={handleSubmit} noValidate>
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="email">
                            {t('login.form.email')}
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={t('login.form.email_placeholder')}
                            className="w-full px-3 py-2 border rounded-md bg-brand-dark border-brand-orange focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-orange-400 font-light" htmlFor="password">
                                {t('login.form.password')}
                            </label>
                            <Link 
                                to="/forgot-password" 
                                className="text-xs text-gray-400 hover:text-orange-400 transition-colors"
                            >
                                {t('login.form.forgot_password', 'Forgot Password?')}
                            </Link>
                        </div>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder={t('login.form.password_placeholder')}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                    </div>

                    {errors.form && <p className="text-red-400 text-center text-sm mb-4">{errors.form}</p>}

                    <button
                        type="submit"
                        className="w-full bg-orange-500 text-white py-2 rounded-md hover:bg-orange-600 transition-colors font-semibold"
                    >
                        {t('login.form.log_in')}
                    </button>
                </form>
                <div className="mt-4 text-center">
                    <p className="text-gray-400">
                        {t('login.form.no_account')}{' '}
                        <Link to="/signup" className="text-orange-400 hover:underline">
                            {t('Signup')}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Login;
